import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useAppFeatures } from '../app-features/api.js';
import { useQuery } from '@tanstack/react-query';
import {
  MEDIA_VOCAB_QUERY_KEY,
  listVocab,
  uploadMedia,
  type HotelMedia,
  type MediaApiError,
} from './api.js';
import type {
  AiInvolvement,
  MediaComplianceFields,
  PeopleConsent,
  RightsBasis,
  Vocab,
  VocabTag,
} from './types.js';
import { Button } from '../../components/ui/button.js';
import { Field } from '../../components/ui/form.js';
import { Input } from '../../components/ui/input.js';
import { Select } from '../../components/ui/select.js';
import { Textarea } from '../../components/ui/textarea.js';
import { TagInput } from '../../components/ui/tag-input.js';
import { CheckIcon, ClockIcon, UploadIcon, WarnIcon } from '../../components/ui/icons.js';
import { cn } from '../../lib/utils.js';
import {
  AI_INVOLVEMENT_LABEL,
  PEOPLE_CONSENT_LABEL,
  RIGHTS_BASIS_LABEL,
  RIGHTS_CONFIRMED_REF_HINT,
  buildNewRightsConfirmation,
  showsAiChangeNote,
  showsRightsConfirmedRef,
  showsStockFields,
  toPeopleRecognizable,
  type PeopleRecognizableChoice,
} from './media-compliance-fields.js';
import { PeopleRecognizableRadioGroup } from './media-people-radio.js';

/** One file's place in the sequential upload run. */
type FileStatus = 'pending' | 'uploading' | 'done' | { error: string };

/**
 * A selected file paired with a stable id assigned at selection time.
 * `file.name` is NOT a safe key — a batch of twenty photos from a shoot
 * routinely has duplicate filenames across source folders, and keying by
 * name would merge their progress into one row.
 */
interface QueuedFile {
  id: number;
  file: File;
}

/**
 * Turns the API's structured failure `code` into a sentence a marketing
 * colleague can act on. `err.message` is not trusted for this — it may carry
 * an internal/English fallback — the codes documented in `./api.ts`'s
 * `MediaErrorBody` are the source of truth.
 */
function describeUploadError(err: unknown): string {
  const code = (err as Partial<MediaApiError> | undefined)?.code;
  switch (code) {
    case 'duplicate_image':
      return 'Ez a kép már fel van töltve ehhez a hotelhez.';
    case 'unknown_tag': {
      const tags = (err as MediaApiError).unknownTags ?? [];
      return `Ismeretlen címke: ${tags.join(', ')}`;
    }
    case 'content_type_unsupported':
      return 'Támogatott formátumok: JPEG, PNG, WebP. HEIC-et nem támogatunk — konvertáld JPEG-re.';
    default:
      return err instanceof Error && err.message ? err.message : 'A feltöltés sikertelen.';
  }
}

/** A file the next "Feltöltés" click will send: never tried, or failed last time. */
function isRetryable(status: FileStatus | undefined): boolean {
  return status === undefined || typeof status === 'object';
}

/**
 * The words the panel may offer and accept (spec 9.3): current tags of a
 * non-system category. `cover` lives in the system category — the hotel
 * cover-image feature writes it, a colleague must not — and an archived
 * word would only earn a 400 `unknown_tag` from the server for a chip the
 * UI itself suggested. Same `archivedAt === null` predicate the detail
 * dialog and the bulk bar use, plus the category check.
 */
function offerableTags(vocab: Vocab): VocabTag[] {
  const systemCategoryIds = new Set(vocab.categories.filter((c) => c.isSystem).map((c) => c.id));
  return vocab.tags.filter((t) => t.archivedAt === null && !systemCategoryIds.has(t.categoryId));
}

function statusIcon(status: FileStatus | undefined) {
  if (!status || status === 'pending')
    return <ClockIcon size={14} className="text-[var(--fg-faint)]" />;
  if (status === 'uploading') return <ClockIcon size={14} className="text-[var(--brand-500)]" />;
  if (status === 'done') return <CheckIcon size={14} className="text-[var(--brand-600)]" />;
  return <WarnIcon size={14} className="text-[var(--danger)]" />;
}

export function MediaUploadPanel({
  hotelId,
  onUploaded,
  compact = false,
  requireAttachable = false,
}: {
  hotelId: string;
  onUploaded: (media: HotelMedia[]) => void;
  /** The post editor attaches right after upload; the gallery does not. */
  compact?: boolean;
  /**
   * The post editor attaches the upload straight away, and an unanswered
   * people question ("Nem tudom" → `null` → `people_unknown` blocker) makes
   * that attach fail after the file is already in the library. With this
   * set the upload button waits for an answer. The default stays `null`:
   * the panel never turns silence into a "no".
   */
  requireAttachable?: boolean;
}) {
  const vocabQuery = useQuery<Vocab>({
    queryKey: MEDIA_VOCAB_QUERY_KEY,
    queryFn: () => listVocab(),
  });
  // `off` (tesztkör-4): the rights questions are not asked at all; the rows
  // are stored with `unknown` and the engine's verdict is discarded server-side.
  const features = useAppFeatures();
  const complianceOff = features.data?.mediaComplianceMode === 'off';
  const vocab = vocabQuery.data ?? { categories: [], tags: [] };
  const addableTags = offerableTags(vocab);

  const nextFileId = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<QueuedFile[]>([]);
  const [progress, setProgress] = useState<Record<number, FileStatus>>({});
  const [isUploading, setIsUploading] = useState(false);
  /** How many files the last batch landed in the library — those rows leave the list. */
  const [lastBatchDone, setLastBatchDone] = useState(0);

  // The two mandatory questions.
  const [rightsBasis, setRightsBasis] = useState<RightsBasis>('unknown');
  const [aiInvolvement, setAiInvolvement] = useState<AiInvolvement>('none');

  // Conditional-on-answer fields.
  const [rightsConfirmedRef, setRightsConfirmedRef] = useState('');
  const [stockPlatform, setStockPlatform] = useState('');
  const [stockTerritory, setStockTerritory] = useState('');
  const [licenseExpiresAt, setLicenseExpiresAt] = useState('');
  const [aiChangeNote, setAiChangeNote] = useState('');
  // Defaults to "unknown", NOT to "no": an upload where nobody answered the
  // people question must arrive with the field absent, so the image carries
  // the `people_unknown` blocker until a human answers it. See
  // `PeopleRecognizableChoice`'s JSDoc for the full reasoning.
  const [peopleChoice, setPeopleChoice] = useState<PeopleRecognizableChoice>('unknown');
  const [peopleConsent, setPeopleConsent] = useState<PeopleConsent>('none');

  // Batch-wide, not compliance: apply to every file the same way.
  const [tags, setTags] = useState<string[]>([]);
  const [caption, setCaption] = useState('');

  const showRightsRef = showsRightsConfirmedRef(rightsBasis);
  const showStockFields = showsStockFields(rightsBasis);
  const showAiChangeNote = showsAiChangeNote(aiInvolvement);
  const showPeopleConsent = peopleChoice === 'yes';
  const peopleAnswerMissing = requireAttachable && peopleChoice === 'unknown';
  const retryableFiles = files.filter((f) => isRetryable(progress[f.id]));

  function addFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    const next = Array.from(list).map((file) => ({ id: nextFileId.current++, file }));
    setFiles((prev) => [...prev, ...next]);
  }

  function removeFile(id: number) {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    setProgress((p) => {
      const rest = { ...p };
      delete rest[id];
      return rest;
    });
  }

  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    addFiles(e.target.files);
    e.target.value = '';
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  }

  function onDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
  }

  function knownTag(tag: string): string | null {
    if (vocabQuery.isError) return 'A címkeszótár nem töltődött be — próbáld újra betölteni.';
    const needle = tag.toLowerCase();
    const row = vocab.tags.find((t) => t.tag.toLowerCase() === needle);
    if (!row) return 'Nincs ilyen címke a szótárban — nem adható hozzá.';
    if (row.archivedAt !== null) return 'Archivált címke — új képre nem adható.';
    if (!addableTags.some((t) => t.id === row.id)) return 'Rendszer-címke — nem adható hozzá.';
    return null;
  }

  function buildCompliance(): MediaComplianceFields {
    return {
      rightsBasis,
      ...buildNewRightsConfirmation(rightsBasis, rightsConfirmedRef),
      aiInvolvement,
      aiChangeNote: showAiChangeNote ? aiChangeNote.trim() || null : null,
      peopleRecognizable: toPeopleRecognizable(peopleChoice),
      peopleConsent: showPeopleConsent ? peopleConsent : null,
      stockPlatform: showStockFields ? stockPlatform.trim() || null : null,
      stockTerritory: showStockFields ? stockTerritory.trim() || null : null,
      licenseExpiresAt: showStockFields ? licenseExpiresAt.trim() || null : null,
    };
  }

  /**
   * Runs one file at a time (`for...of await`), never `Promise.all` — a
   * batch of twenty must not abort because photo #3 turned out to be a
   * duplicate. Every file gets its own status; a caption/tag/compliance
   * default from the batch applies to all of them identically.
   */
  async function handleUpload() {
    if (retryableFiles.length === 0 || isUploading || peopleAnswerMissing) return;
    setIsUploading(true);
    setLastBatchDone(0);
    const compliance = buildCompliance();
    const uploaded: HotelMedia[] = [];
    const doneIds: number[] = [];
    // Only files never sent or failed last time: a second click after a
    // batch used to resend the already-uploaded rows too, and every one of
    // them came back as a 409 duplicate.
    for (const { id, file } of retryableFiles) {
      setProgress((p) => ({ ...p, [id]: 'uploading' }));
      try {
        const media = await uploadMedia(hotelId, file, {
          tags,
          caption: caption.trim() || null,
          compliance,
        });
        uploaded.push(media);
        doneIds.push(id);
        setProgress((p) => ({ ...p, [id]: 'done' }));
      } catch (err) {
        setProgress((p) => ({ ...p, [id]: { error: describeUploadError(err) } }));
      }
    }
    // The landed files leave the list; the failed ones stay with their
    // error, removable or retryable one by one.
    setFiles((prev) => prev.filter((f) => !doneIds.includes(f.id)));
    setLastBatchDone(doneIds.length);
    setIsUploading(false);
    if (uploaded.length > 0) onUploaded(uploaded);
  }

  return (
    <section
      className={cn(
        'space-y-4 rounded-lg border border-[var(--app-divider)] bg-[var(--app-surface)]',
        compact ? 'p-3' : 'p-5',
      )}
    >
      {!compact && (
        <div>
          <h2 className="text-h2 font-semibold text-[var(--ink-700)]">Képek feltöltése</h2>
          {!complianceOff && (
            <p className="mt-1 text-meta text-[var(--fg-2)]">
              A két kötelező kérdés a teljes kötegre vonatkozik — minden ide feltöltött fájl
              ugyanazt a jogalapot és AI-érintettséget kapja.
            </p>
          )}
        </div>
      )}

      <div className={cn('grid gap-4', compact ? 'grid-cols-1' : 'sm:grid-cols-2')}>
        {!complianceOff && (
          <>
            <Field label="Jogalap" htmlFor="mup-rights-basis">
              <Select
                id="mup-rights-basis"
                value={rightsBasis}
                onChange={(e) => setRightsBasis(e.target.value as RightsBasis)}
              >
                {(Object.keys(RIGHTS_BASIS_LABEL) as RightsBasis[]).map((v) => (
                  <option key={v} value={v}>
                    {RIGHTS_BASIS_LABEL[v]}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="AI-érintettség" htmlFor="mup-ai-involvement">
              <Select
                id="mup-ai-involvement"
                value={aiInvolvement}
                onChange={(e) => setAiInvolvement(e.target.value as AiInvolvement)}
              >
                {(Object.keys(AI_INVOLVEMENT_LABEL) as AiInvolvement[]).map((v) => (
                  <option key={v} value={v}>
                    {AI_INVOLVEMENT_LABEL[v]}
                  </option>
                ))}
              </Select>
            </Field>

            {showRightsRef && (
              <Field
                label="Megerősítés hivatkozása"
                htmlFor="mup-rights-ref"
                hint={RIGHTS_CONFIRMED_REF_HINT}
              >
                <Input
                  id="mup-rights-ref"
                  value={rightsConfirmedRef}
                  onChange={(e) => setRightsConfirmedRef(e.target.value)}
                />
              </Field>
            )}

            {showStockFields && (
              <>
                <Field label="Stock platform" htmlFor="mup-stock-platform">
                  <Input
                    id="mup-stock-platform"
                    value={stockPlatform}
                    onChange={(e) => setStockPlatform(e.target.value)}
                    placeholder="pl. Shutterstock"
                  />
                </Field>
                <Field label="Terület" htmlFor="mup-stock-territory">
                  <Input
                    id="mup-stock-territory"
                    value={stockTerritory}
                    onChange={(e) => setStockTerritory(e.target.value)}
                    placeholder="pl. világ / EU / HU"
                  />
                </Field>
                <Field label="Licenc lejárata" htmlFor="mup-license-expires">
                  <Input
                    id="mup-license-expires"
                    type="date"
                    value={licenseExpiresAt}
                    onChange={(e) => setLicenseExpiresAt(e.target.value)}
                  />
                </Field>
              </>
            )}

            {showAiChangeNote && (
              <Field label="Mi módosult?" htmlFor="mup-ai-change-note">
                <Input
                  id="mup-ai-change-note"
                  value={aiChangeNote}
                  onChange={(e) => setAiChangeNote(e.target.value)}
                  placeholder="pl. háttér cserélve, tárgy eltávolítva"
                />
              </Field>
            )}

            <div className="sm:col-span-2 space-y-1.5">
              <PeopleRecognizableRadioGroup
                namePrefix="mup"
                value={peopleChoice}
                onChange={setPeopleChoice}
              />
              {peopleAnswerMissing && (
                <p className="text-meta text-[var(--danger)]">
                  A poszthoz csatoláshoz válaszold meg a személy-kérdést — „Nem tudom" mellett a kép
                  a könyvtárba kerül, de nem csatolható.
                </p>
              )}
            </div>

            {showPeopleConsent && (
              <Field label="Hozzájárulás" htmlFor="mup-people-consent">
                <Select
                  id="mup-people-consent"
                  value={peopleConsent}
                  onChange={(e) => setPeopleConsent(e.target.value as PeopleConsent)}
                >
                  {(Object.keys(PEOPLE_CONSENT_LABEL) as PeopleConsent[]).map((v) => (
                    <option key={v} value={v}>
                      {PEOPLE_CONSENT_LABEL[v]}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </>
        )}

        <div className="sm:col-span-2">
          <div className="space-y-1.5">
            <label className="text-body font-medium text-[var(--ink-700)]">Címkék</label>
            <TagInput value={tags} onChange={setTags} validate={knownTag} />
            {vocabQuery.isError && (
              <p role="alert" className="flex items-center gap-2 text-meta text-[var(--danger)]">
                A címkeszótár betöltése nem sikerült, címke most nem adható.
                <button
                  type="button"
                  onClick={() => void vocabQuery.refetch()}
                  className="underline hover:text-[var(--ink-700)]"
                >
                  Újra
                </button>
              </p>
            )}
            {addableTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {addableTags
                  .filter((t) => !tags.includes(t.tag))
                  .slice(0, 12)
                  .map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTags((prev) => [...prev, t.tag])}
                      className="rounded-pill border border-[var(--app-divider-2)] px-2 py-0.5 text-meta text-[var(--fg-2)] hover:bg-[var(--app-hover)]"
                    >
                      {t.tag}
                    </button>
                  ))}
              </div>
            )}
          </div>
        </div>

        <div className="sm:col-span-2">
          <Field label="Felirat" htmlFor="mup-caption">
            <Textarea
              id="mup-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={compact ? 2 : 3}
            />
          </Field>
        </div>
      </div>

      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        className="space-y-3 rounded-md border border-dashed border-[var(--app-divider-2)] p-4 text-center"
      >
        <div className="flex flex-col items-center gap-1.5 text-[var(--fg-2)]">
          <UploadIcon size={20} />
          <p className="text-body">Húzd ide a fájlokat, vagy válaszd ki őket.</p>
        </div>
        {/*
          The native file input stays in the tree (screen readers, tests and
          drag-and-drop all go through it) but off-screen: its browser-drawn
          "Nincs fájl kiválasztva" widget cannot be styled, so a real button
          opens it instead. The chosen files are listed below, so the native
          "N fájl" caption is not missed.
        */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          aria-label="Fájlok"
          className="sr-only"
          onChange={onInputChange}
        />
        <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
          Fájlok kiválasztása
        </Button>

        {files.length > 0 && (
          <ul className="space-y-1 text-left">
            {files.map(({ id, file }) => {
              const status = progress[id];
              return (
                <li key={id} className="space-y-0.5">
                  <div className="flex items-center justify-between gap-2 text-body text-[var(--ink-700)]">
                    <span className="flex items-center gap-2">
                      {statusIcon(status)}
                      <span>{file.name}</span>
                    </span>
                    {!isUploading && (
                      <button
                        type="button"
                        aria-label={`${file.name} eltávolítása`}
                        onClick={() => removeFile(id)}
                        className="text-[var(--fg-faint)] hover:text-[var(--danger)]"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {typeof status === 'object' && (
                    <p className="text-meta text-[var(--danger)]">
                      {file.name}: {status.error}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-end gap-3">
        {lastBatchDone > 0 && !isUploading && (
          <p role="status" className="text-meta text-[var(--fg-2)]">
            {lastBatchDone} kép feltöltve a könyvtárba.
          </p>
        )}
        <Button
          type="button"
          disabled={retryableFiles.length === 0 || isUploading || peopleAnswerMissing}
          onClick={handleUpload}
        >
          {isUploading ? 'Feltöltés…' : 'Feltöltés'}
        </Button>
      </div>
    </section>
  );
}
